import { useState } from 'react';
import {
    Chip,
    Collapse,
    Grid,
    Typography,
    Box,
    IconButton,
    Paper,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import FormTextField from '../../components/FormFields/FormTextField';
import UpdateButton from './adminUtils/updateButton';
import SearchBar from './adminUtils/SearchBar';
import PaginationInfo from './PaginationInfo';
import { dateUtils } from '../../utils';

function BugiraportitList({ bugiraportit, handleDeleteBugiraportti }) {
    const [selectedBugiraportti, setSelectedBugiraportti] = useState(null);
    const [newBugiraportitData, setNewBugiraportitData] = useState({
        otsikko: '',
        raportti: '',
        lähetetty: null,
        lisatiedot: '',
        user: { name: '', id: null },
    });
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const itemsPerPage = 10;

    const handleSearchChange = (newSearchTerm) => {
        setSearchTerm(newSearchTerm);
        setCurrentPage(1);
    };

    const filteredBugiraportit = bugiraportit?.filter((Bugiraportti) =>
        Bugiraportti.otsikko.toLowerCase().includes(searchTerm.toLowerCase())
    ) || [];
    const totalPages = Math.ceil(filteredBugiraportit.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const displayedBugiraportit = filteredBugiraportit.slice(startIndex, endIndex);

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const handleUserClick = (bugiraportti) => {
        if (selectedBugiraportti?.id === bugiraportti.id) {
            setSelectedBugiraportti(null);
            setNewBugiraportitData({ otsikko: '', raportti: '', lähetetty: null, lisatiedot: '', user: { name: '', id: null } });
        } else {
            setSelectedBugiraportti(bugiraportti);
            setNewBugiraportitData(bugiraportti);
        }
    };

    return (
        <Box sx={{ py: 3 }}>
            <Paper elevation={2} sx={{ p: 3, borderRadius: 2, bgcolor: 'background.paper' }}>
                {/* Header and Search */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontWeight: 500 }}>
                            Bugiraportit
                        </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <SearchBar searchTerm={searchTerm} onSearchChange={handleSearchChange} />
                    </Grid>
                </Grid>

                {/* Bug Reports List */}
                {displayedBugiraportit.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                        Ei bugiraportteja näytettäväksi.
                    </Typography>
                ) : (
                    displayedBugiraportit.map((bugiraportti) => {
                        const isSelected = selectedBugiraportti?.id === bugiraportti.id;
                        const summaryText = [bugiraportti.user?.name, dateUtils.getFormattedDateWithHours(bugiraportti.lähetetty)]
                            .filter(Boolean)
                            .join(' | ');

                        return (
                            <Box key={bugiraportti.id} sx={{ mb: 1.5 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        borderRadius: 2,
                                        border: '1px solid',
                                        borderColor: isSelected ? 'taphaBlue.main' : 'grey.200',
                                        bgcolor: isSelected ? 'rgba(25, 118, 210, 0.06)' : 'taphaWhite.main',
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        justifyContent: 'space-between',
                                        gap: 2,
                                        '&:hover': {
                                            bgcolor: isSelected ? 'rgba(25, 118, 210, 0.08)' : 'grey.50',
                                            boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
                                            cursor: 'pointer',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                    onClick={() => handleUserClick(bugiraportti)}
                                >
                                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                            {bugiraportti.otsikko}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                            {summaryText || 'Ei lähettäjätietoa'}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
                                            {bugiraportti.lisatiedot && <Chip size="small" label="Lisätiedot" />}
                                        </Box>
                                    </Box>
                                    <IconButton
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            handleDeleteBugiraportti(event, bugiraportti);
                                            if (selectedBugiraportti?.id === bugiraportti.id) {
                                                setSelectedBugiraportti(null);
                                                setNewBugiraportitData({
                                                    otsikko: '',
                                                    raportti: '',
                                                    lähetetty: null,
                                                    lisatiedot: '',
                                                    user: { name: '', id: null },
                                                });
                                            }
                                        }}
                                        sx={{ color: 'error.main', flexShrink: 0 }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>

                                <Collapse in={isSelected} timeout="auto" unmountOnExit>
                                    <Box
                                        sx={{
                                            mt: 1,
                                            p: 2,
                                            border: '1px solid',
                                            borderColor: 'grey.200',
                                            borderRadius: 2,
                                            bgcolor: 'grey.50',
                                        }}
                                    >
                                        <Typography variant="subtitle1" sx={{ mb: 2 }}>
                                            Bugiraportin tiedot
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="otsikko"
                                                    label="Otsikko"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newBugiraportitData.otsikko}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="raportti"
                                                    label="Raportti"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newBugiraportitData.raportti}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="lähetetty"
                                                    label="Lähetetty"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={dateUtils.getFormattedDateWithHours(newBugiraportitData.lähetetty)}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="lisatiedot"
                                                    label="Lisätiedot"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newBugiraportitData.lisatiedot}
                                                    disabled
                                                />
                                            </Grid>
                                            <Grid item xs={12} sm={6}>
                                                <FormTextField
                                                    fieldName="user"
                                                    label="Lähettänyt"
                                                    multiline={true}
                                                    fullWidth={true}
                                                    value={newBugiraportitData.user?.name}
                                                    disabled
                                                />
                                            </Grid>
                                        </Grid>
                                    </Box>
                                </Collapse>
                            </Box>
                        );
                    })
                )}

                {/* Pagination */}
                {filteredBugiraportit.length > itemsPerPage && (
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <PaginationInfo
                            currentPage={currentPage}
                            itemsPerPage={itemsPerPage}
                            filteredArray={filteredBugiraportit}
                            array={bugiraportit}
                        />
                        <Box sx={{ mt: 1 }}>
                            <IconButton
                                size="small"
                                onClick={handlePreviousPage}
                                disabled={currentPage <= 1}
                                sx={{ mr: 1 }}
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <IconButton
                                size="small"
                                onClick={handleNextPage}
                                disabled={currentPage >= totalPages}
                            >
                                <ArrowForwardIcon />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Paper>
        </Box>
    );
}

export default BugiraportitList;